/**
 * @module app.cm
 */

import { apiCall } from 'app/shared/lib/api/api';
import { autoinject, bindable, bindingMode, TaskQueue } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection, IForm } from 'app/shared/lib/essette-site';
import { CustomFieldContext } from 'app/core/lib/custom-field-context';
import { deepClone } from 'app/shared/lib/utilities';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { EssTableCustomElement, ExpandEvent } from 'app/shared/elements/ess-table';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { IBaseConfig } from 'app/core/models/essette-interfaces';
import { ICustomFieldTargetColumnsContainer } from 'app/shared/lib/custom-field-definitions'
import { IPopup } from 'app/shared/elements/popup-base';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { LastEligibilityProductDeletedReloadEvent } from 'app/core/lib/events';
import { MemberService } from 'app/cd/services/member-service';
import { SaveEligibilityMemberPlan } from 'app/core/components/member/save-eligibility-member-plan';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { validate } from 'app/shared/elements/ess-rule';
import { MemberDisplayChangeEvent } from 'app/cd/lib/events';
import * as CD from 'app/cd/models/essette-api';

@autoinject
export class memberEligibility extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable
    protected config: IBaseConfig = {};
    protected includeInactive = false;
    protected pageSize = 10;
    protected resultCount = 0;
    protected rememberExpand: boolean = true;
    protected sorting = new Sorting();

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected memberId: string | null = null;
    protected memberPlans: ReadonlyArray<CD.MemberPlan> = [];
    protected memberPlanDetails: Record<string, boolean> = {};
    protected customFieldTargetColumns: ICustomFieldTargetColumnsContainer | null = null;

    protected validateForm = validate;
    private lastEligibilityProductDeleted: Subscription | null = null;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $table!: EssTableCustomElement<CD.MemberPlan[]>;
    protected $form!: IForm;
    protected $saveMemberPlanModal!: IPopup;
    protected $saveMemberPlan!: SaveEligibilityMemberPlan;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly customFieldContext: CustomFieldContext,
        private readonly queue: TaskQueue,
        private readonly service: MemberService,
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);

        // Default Sort
        this.sorting.entries = [
            {
                reference: 'eligibility.rank',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected bind(): void {
        this.lastEligibilityProductDeleted = this.events.subscribe(LastEligibilityProductDeletedReloadEvent, async (e: LastEligibilityProductDeletedReloadEvent) => {
            const oldPrimaryPlanId = this.memberPlans.find(plan => plan.isPrimary)?.identifier?.primary;
            await this.reload();
            const newPrimaryPlanId = this.memberPlans.find(plan => plan.isPrimary)?.identifier?.primary;
            // tell page header to refresh the member display only if primary plan ID has changed
            if(oldPrimaryPlanId !== newPrimaryPlanId) this.events.publish(new MemberDisplayChangeEvent());
        });
        this.loadCustomFieldTargets();
        this.reload();
    }

    protected unbind(): void {
        if(this.lastEligibilityProductDeleted) {
            this.lastEligibilityProductDeleted.dispose();
        }
    }

    ////****************** Events ***************************************************////
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
            if(this.sorting.entries.length == 0) {
                this.sorting.entries = [
                    {
                        reference: 'eligibility.rank',
                        direction: SortDirection.Ascending
                    }
                ];
            }
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    protected async formSuccess({ detail }: FormSuccessCustomEvent): Promise<void> {
        this.$saveMemberPlanModal.close();

        if(detail.form.reset) detail.form.reset();

        const oldPrimaryPlanId = this.memberPlans.find(plan => plan.isPrimary)?.identifier?.primary;
        // reload grid and reset page
        await this.reload();
        const newPrimaryPlanId = this.memberPlans.find(plan => plan.isPrimary)?.identifier?.primary;
        // tell page header to refresh the member display only if primary plan ID has changed
        if(oldPrimaryPlanId !== newPrimaryPlanId) this.events.publish(new MemberDisplayChangeEvent());
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadMemberPlans();
    }

    private restoreExpanded() {
        if(!this.rememberExpand) { return; }
        this.queue.queueTask(() => {
            this.$table.restoreExpanded(this.$table.expanded as Array<CD.MemberPlan>);
        });
    }

    protected saveMemberPlan(plan: CD.MemberPlan): void {
        this.$saveMemberPlan.loadMemberPlan(deepClone(plan));
        this.$saveMemberPlanModal.open();
    }

    protected saveMemberPlanClose(): void {
        if(this.$form.reset) this.$form.reset();
        this.$saveMemberPlanModal.close();
    }

    protected memberDetailExpanded(event: ExpandEvent<CD.MemberEligibility>) {
        const item = event.detail.item;
        const items = event.detail.items;

        if(item && items.includes(item)) {
            this.memberPlanDetails[item.id!] = true;
        }
    }

    ////****************** Service Calls ********************************************////
    private async loadCustomFieldTargets(): Promise<void> {
        this.customFieldTargetColumns = await this.customFieldContext.getCustomFieldTargetColumns('CD.MemberPlan');
    }

    @apiCall
    private async loadMemberPlans(): Promise<void> {
        if(!this.memberId) return;
        const includes: string[] = [
            'eligibility.history',
            'eligibility.history.product',
            'healthPlan',
            'lineOfBusiness',
        ];
        const fields: string[] = [
            'custom.text.*',
            'custom.lookup.*',
            'eligibility.rank',
            'eligibility.history.product.$display',
            'eligibility.history.dateRange.*',
            'eligibility.history.receivedDate',
            'eligibility.history.employer',
            'eligibility.history.groupNumber',
            'eligibility.history.shareOfCostAmount',
            'identifier.primary',
            'isActive',
            'isPrimary',
            'healthPlan.$display',
            'lineOfBusiness.$display'
        ];
        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            sorting: this.sorting,
            page: this.$pager?.page || 0,
            includeInactive: this.includeInactive
        };

        try {
            this.state.beginLoad();
            const response = await this.service.loadPlansByMember(this.memberId, options, includes, fields);
            this.memberPlans = response.results;
            this.resultCount = response.count || 0;
            this.restoreExpanded();
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
